import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVerticalIcon, ChevronDown, ChevronUp, ChevronsUpDown, ArchiveIcon, FileEditIcon, PlayCircleIcon, MegaphoneIcon, SearchIcon, PlusIcon } from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { Button } from "@component/button";
import { Card } from "@component/card";
import { Pagination } from "@component/pagination";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@component/dialog";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@component/sheet";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "@component/dropdown-menu";
import { RubricEditor } from "@container/training/rubric-editor";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import {
    listCampaigns,
    pickPersonaName,
    pickPersonaDescription,
    updateCampaignStatus,
    getRubric,
    updateRubric,
    updateCampaign,
    getWorkspaceTeamLeaders,
} from "@service/training";
import type { WorkspaceTeamLeader } from "@service/training";
import {
    TrainingErrorCard,
    TrainingSkeletonRows,
} from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import type { TrainingCampaign, RubricCriterion, CampaignStatus } from "@type/training";
import { TRAINING_TYPE_LABEL } from "@type/training";
import { cn } from "@util/tailwind";
import { CampaignStatusBadge } from "@component/training/campaign-status-badge";

type SortKey = "name" | "created_at" | "avg_score" | "completed_pct";
type SortDir = "asc" | "desc";
type TabFilter = "active" | "inactive";

const CAMPAIGNS_PER_PAGE = 20;

// Maps a save failure to a plain-language message for the candidate-facing
// banner. Never surfaces raw status codes or backend payloads — those go to
// Sentry via catchErrorLog. Reads the HTTP status defensively (no axios
// import needed) so it works for both axios errors and anything else thrown.
function toFriendlySaveError(err: unknown): string {
    const status = (err as { response?: { status?: number } })?.response?.status;
    switch (status) {
        case 400:
            return "Please check the campaign details and try again.";
        case 401:
            return "Your session has expired. Please sign in again.";
        case 403:
            return "You don't have permission to edit this campaign.";
        case 404:
            return "This campaign is no longer available. It may have been removed.";
        case 408:
        case 504:
            return "The request took too long. Please check your connection and try again.";
        default:
            if (status && status >= 500) {
                return "Something went wrong on our side. Please try again in a moment.";
            }
            return "We couldn't save your changes. Please try again.";
    }
}

// Copy + iconography per status transition shown in the confirm dialog.
// Note on "Archive"/"Draft" gating: as of BE staging commit cd97716a
// ("improve API campaign draft and archive", 2026-05-29) the trainee
// `POST /session/{token}/start` endpoint gates on campaign.status — it returns
// 403 for both archived ("retired") and draft ("not launched yet") campaigns.
// So existing links are NOT live while a campaign sits in draft/archived; they
// only work again once the campaign is reactivated. `body` is the first line
// (prefixed with the campaign name); `body2` is an optional second paragraph.
const STATUS_CHANGE_COPY: Record<
    CampaignStatus,
    {
        title: string;
        body: string;
        body2?: string;
        actionLabel: string;
        actionGerund: string;
        icon: typeof ArchiveIcon;
        iconBg: string;
        iconColor: string;
    }
> = {
    archived: {
        title: "Archive campaign?",
        body: "will be archived and appear under Inactive Campaigns.",
        body2: "Existing session links will be disabled, and no new trainees can be added. All past results are saved, and you can reactivate at any time.",
        actionLabel: "Archive",
        actionGerund: "Archiving",
        icon: ArchiveIcon,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
    },
    draft: {
        title: "Convert to draft?",
        body: "will be converted to draft and appear under Inactive Campaigns.",
        body2: "Trainees who already have a link can still complete their roleplay. You can continue adding trainees, but they won't receive an invitation until you publish the campaign again.",
        actionLabel: "Convert to draft",
        actionGerund: "Converting",
        icon: FileEditIcon,
        iconBg: "bg-gray-100",
        iconColor: "text-gray-600",
    },
    active: {
        title: "Reactivate campaign?",
        body: "will be moved back to Active Campaigns and shown alongside your other active campaigns.",
        actionLabel: "Reactivate",
        actionGerund: "Reactivating",
        icon: PlayCircleIcon,
        iconBg: "bg-emerald-50",
        iconColor: "text-emerald-600",
    },
};

// Publishing a draft also flips status to "active", but the framing differs
// from reactivating an archived campaign — it's the first launch and sends
// invitations. Used when the source status is "draft".
const PUBLISH_COPY: (typeof STATUS_CHANGE_COPY)[CampaignStatus] = {
    title: "Publish campaign?",
    body: "will go live and invitations will be sent to the employees on this campaign. You can convert it back to draft later if needed.",
    actionLabel: "Publish",
    actionGerund: "Publishing",
    icon: PlayCircleIcon,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function initials(name: string | undefined | null): string {
    if (!name) return "?";
    return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

const TL_NONE = "__none__";

export default function TrainingCampaignListPage() {
    const navigate = useNavigate();
    const [, currentUser] = useUserProfileContext();
    const canWrite = currentUser?.permission !== "member";
    const isOwner = currentUser?.permission === "owner";
    // Trial/usage gate for campaign create + edit (training-agent workspaces).
    const { isTrainingCampaignBlocked } = useSubscription();
    const campaignBlockedTitle = isTrainingCampaignBlocked
        ? "Your trial has expired or your campaign limit is reached. Upgrade to create or edit campaigns."
        : undefined;
    const [campaigns, setCampaigns] = useState<TrainingCampaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadTick, setReloadTick] = useState(0);

    const [tabFilter, setTabFilter] = useState<TabFilter>("active");
    const [sortKey, setSortKey] = useState<SortKey>("created_at");
    const [sortDir, setSortDir] = useState<SortDir>("desc");

    const [searchQuery, setSearchQuery] = useState("");
    const [tlFilter, setTlFilter] = useState("");

    const [activePage, setActivePage] = useState(1);
    const [draftPage, setDraftPage] = useState(1);
    const [archivedPage, setArchivedPage] = useState(1);

    const [editTarget, setEditTarget] = useState<TrainingCampaign | null>(null);
    const [editName, setEditName] = useState("");
    const [originalName, setOriginalName] = useState("");
    const [editTeamLeader, setEditTeamLeader] = useState("");
    const [originalTeamLeader, setOriginalTeamLeader] = useState("");
    const [editRubric, setEditRubric] = useState<RubricCriterion[]>([]);
    const [originalRubric, setOriginalRubric] = useState<RubricCriterion[]>([]);
    const [rubricLoading, setRubricLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [teamLeaderOptions, setTeamLeaderOptions] = useState<WorkspaceTeamLeader[]>([]);

    const [savedToast, setSavedToast] = useState<string | null>(null);

    const [saveError, setSaveError] = useState<string | null>(null);

    // One generalized "change campaign status" flow handles archive, convert
    // to draft, and reactivate. Each menu action sets a target campaign +
    // the status to move it to, opening the same confirmation dialog with
    // copy that adapts to the transition.
    const [statusTarget, setStatusTarget] = useState<
        { campaign: TrainingCampaign; nextStatus: CampaignStatus } | null
    >(null);
    const [statusChanging, setStatusChanging] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        listCampaigns()
            .then((c) => {
                if (cancelled) return;
                setCampaigns(c);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[training] campaign list load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [reloadTick]);

    useEffect(() => {
        getWorkspaceTeamLeaders().then(setTeamLeaderOptions).catch(() => {});
    }, []);

    useEffect(() => {
        if (!editTarget) return;
        let cancelled = false;
        setRubricLoading(true);
        setSaveError(null);
        getRubric(editTarget.id)
            .then((criteria) => {
                if (cancelled) return;
                const allZero = criteria.every((c) => c.weight === 0);
                const rubric = allZero ? criteria.map((c) => ({ ...c, weight: 20 })) : criteria;
                setEditRubric(rubric);
                setOriginalRubric(rubric);
                setRubricLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                catchErrorLog(`[training] getRubric failed: ${err instanceof Error ? err.message : String(err)}`);
                setSaveError("Failed to load scoring rubric. Please close and try again.");
                setRubricLoading(false);
            });
        return () => { cancelled = true; };
    }, [editTarget]);

    const openEdit = (campaign: TrainingCampaign) => {
        setEditName(campaign.name);
        setOriginalName(campaign.name);
        setEditTeamLeader(campaign.team_leader ?? "");
        setOriginalTeamLeader(campaign.team_leader ?? "");
        setEditTarget(campaign);
    };

    const closeEdit = () => {
        setEditTarget(null);
        setEditName("");
        setOriginalName("");
        setEditTeamLeader("");
        setOriginalTeamLeader("");
        setEditRubric([]);
        setOriginalRubric([]);
        setSaveError(null);
    };

    const handleSave = async () => {
        if (!editTarget) return;
        setSaving(true);
        setSaveError(null);
        try {
            const calls: Promise<unknown>[] = [];
            const nameChanged = editName.trim() !== originalName;
            const tlChanged = editTeamLeader !== originalTeamLeader;
            if (nameChanged || tlChanged) {
                calls.push(updateCampaign(editTarget.id, {
                    name: editName.trim(),
                    team_leader: editTeamLeader.trim() || null,
                }));
            }
            if (JSON.stringify(editRubric) !== JSON.stringify(originalRubric)) {
                calls.push(
                    updateRubric(
                        editTarget.id,
                        editRubric.map((c) => ({ ...c, weight: c.weight }))
                    )
                );
            }
            await Promise.all(calls);
            closeEdit();
            setReloadTick((t) => t + 1);
            setSavedToast("Campaign updated successfully.");
            setTimeout(() => setSavedToast(null), 4000);
        } catch (err: unknown) {
            catchErrorLog(`[training] edit campaign save failed: ${err instanceof Error ? err.message : String(err)}`);
            setSaveError(toFriendlySaveError(err));
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async () => {
        if (!statusTarget) return;
        setStatusChanging(true);
        try {
            await updateCampaignStatus(statusTarget.campaign.id, statusTarget.nextStatus);
            setStatusTarget(null);
            setReloadTick((t) => t + 1);
        } finally {
            setStatusChanging(false);
        }
    };

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "created_at" ? "desc" : "asc");
        }
    };

    const activeCampaigns = useMemo(
        () => campaigns.filter((c) => c.status === "active"),
        [campaigns],
    );
    // Inactive tab now splits into two sub-sections: Drafts (pre-launch,
    // editable, not yet sending invites) and Archived (retired, read-only).
    const draftCampaigns = useMemo(
        () => campaigns.filter((c) => c.status === "draft"),
        [campaigns],
    );
    const archivedCampaigns = useMemo(
        () => campaigns.filter((c) => c.status === "archived"),
        [campaigns],
    );
    const inactiveCount = draftCampaigns.length + archivedCampaigns.length;

    const sortCampaigns = useCallback((list: TrainingCampaign[]) => {
        return [...list].sort((a, b) => {
            let av: number | string;
            let bv: number | string;
            if (sortKey === "name") {
                av = a.name.toLowerCase();
                bv = b.name.toLowerCase();
            } else if (sortKey === "created_at") {
                av = a.created_at;
                bv = b.created_at;
            } else if (sortKey === "avg_score") {
                av = a.avg_score ?? -1;
                bv = b.avg_score ?? -1;
            } else {
                av = a.employee_count > 0 ? a.completed_count / a.employee_count : -1;
                bv = b.employee_count > 0 ? b.completed_count / b.employee_count : -1;
            }
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [sortKey, sortDir]);

    const activeSorted = useMemo(() => sortCampaigns(activeCampaigns), [sortCampaigns, activeCampaigns]);
    const draftSorted = useMemo(() => sortCampaigns(draftCampaigns), [sortCampaigns, draftCampaigns]);
    const archivedSorted = useMemo(() => sortCampaigns(archivedCampaigns), [sortCampaigns, archivedCampaigns]);

    // Client-side search + TL filter applied after sort.
    const applyFilters = useCallback((list: TrainingCampaign[]) => {
        let out = list;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            out = out.filter((c) => c.name.toLowerCase().includes(q));
        }
        if (tlFilter) {
            out = out.filter((c) => (c.team_leader ?? "") === tlFilter);
        }
        return out;
    }, [searchQuery, tlFilter]);

    const filteredActive = useMemo(() => applyFilters(activeSorted), [applyFilters, activeSorted]);
    const filteredDraft = useMemo(() => applyFilters(draftSorted), [applyFilters, draftSorted]);
    const filteredArchived = useMemo(() => applyFilters(archivedSorted), [applyFilters, archivedSorted]);

    // TL options with per-option campaign counts for the filter dropdown.
    // Counts are scoped to the current tab so active and inactive totals don't bleed into each other.
    const tlFilterOptions = useMemo(() => {
        const base = tabFilter === "active"
            ? activeCampaigns
            : [...draftCampaigns, ...archivedCampaigns];
        const counts = new Map<string, number>();
        for (const c of base) {
            if (c.team_leader) {
                counts.set(c.team_leader, (counts.get(c.team_leader) ?? 0) + 1);
            }
        }
        return Array.from(counts.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }));
    }, [tabFilter, activeCampaigns, draftCampaigns, archivedCampaigns]);

    // Reset to page 1 whenever filters or the active tab changes.
    useEffect(() => { setActivePage(1); setDraftPage(1); setArchivedPage(1); }, [searchQuery, tlFilter, tabFilter]);

    const pagedActive   = filteredActive.slice((activePage - 1) * CAMPAIGNS_PER_PAGE, activePage * CAMPAIGNS_PER_PAGE);
    const pagedDraft    = filteredDraft.slice((draftPage - 1) * CAMPAIGNS_PER_PAGE, draftPage * CAMPAIGNS_PER_PAGE);
    const pagedArchived = filteredArchived.slice((archivedPage - 1) * CAMPAIGNS_PER_PAGE, archivedPage * CAMPAIGNS_PER_PAGE);

    const rubricWeightSum = editRubric.reduce((s, c) => s + c.weight, 0);
    const hasChanges = editName !== originalName || editTeamLeader !== originalTeamLeader || JSON.stringify(editRubric) !== JSON.stringify(originalRubric);
    const canSave = editName.trim().length > 0 && rubricWeightSum === 100 && !rubricLoading && hasChanges;

    // Renders a campaign table for a given (already-sorted+filtered) list.
    // Column order: Campaign | Scenario | Completion | Avg Score | Created | Team Leader | Status | [actions]
    const renderCampaignTable = (rows: TrainingCampaign[]) => (
        <Card className="overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <SortableTh label="Campaign" sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort} align="left" />
                            <SortableTh label="Scenario" sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort} align="left" disabled />
                            <SortableTh label="Completion" sortKey="completed_pct" current={sortKey} dir={sortDir} onSort={toggleSort} align="center" widthClass="w-px whitespace-nowrap" />
                            <SortableTh label="Avg Score" sortKey="avg_score" current={sortKey} dir={sortDir} onSort={toggleSort} align="center" widthClass="w-px whitespace-nowrap" />
                            <SortableTh label="Created" sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort} align="center" widthClass="w-px whitespace-nowrap" />
                            <th className="h-[45px] px-4 py-3 w-px whitespace-nowrap text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Team Leader</th>
                            <SortableTh label="Status" sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort} align="left" disabled widthClass="w-px whitespace-nowrap" />
                            <th className="h-[45px] px-4 py-3 w-10" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {rows.map((c) => {
                            const pct = c.employee_count > 0
                                ? Math.round((c.completed_count / c.employee_count) * 100)
                                : 0;
                            const scenarioName = c.persona_name ?? pickPersonaName(c.persona_id);
                            const scenarioDesc = c.persona_description ?? pickPersonaDescription(c.persona_id);
                            return (
                            <tr
                                key={c.id}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => navigate(`/training/campaigns/${c.id}`)}
                            >
                                <td className="px-4 py-4 whitespace-nowrap max-w-[260px]">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                            <MegaphoneIcon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                            {c.name}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 align-top min-w-[360px] max-w-[460px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-gray-900">
                                            {scenarioName}
                                        </span>
                                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 whitespace-nowrap">
                                            {TRAINING_TYPE_LABEL[c.training_type]}
                                        </span>
                                    </div>
                                    {scenarioDesc && (
                                        <div className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-snug">
                                            {scenarioDesc}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-4 w-px whitespace-nowrap text-center text-sm text-gray-500">
                                    {c.completed_count}/{c.employee_count} ({pct}%)
                                </td>
                                <td className="px-4 py-4 w-px whitespace-nowrap text-center text-sm text-gray-500">
                                    {c.avg_score !== null ? `${Math.round(c.avg_score)}/100` : "—"}
                                </td>
                                <td className="px-4 py-4 w-px whitespace-nowrap text-center text-sm text-gray-500">
                                    {formatDate(c.created_at)}
                                </td>
                                <td className="px-4 py-4 w-px whitespace-nowrap text-center">
                                    {c.team_leader ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                                {initials(c.team_leader)}
                                            </div>
                                            <span className="text-sm text-gray-700">{c.team_leader}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-sm">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-4 w-px whitespace-nowrap">
                                    <CampaignStatusBadge status={c.status} />
                                </td>
                                <td
                                    className="px-4 py-4 whitespace-nowrap text-center"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {canWrite && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                                                    <MoreVerticalIcon className="w-4 h-4" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                {/* Archived is read-only — no Edit. Draft/Active can be edited. */}
                                                {c.status !== "archived" && (
                                                    <DropdownMenuItem onSelect={() => openEdit(c)}>
                                                        Edit campaign
                                                    </DropdownMenuItem>
                                                )}
                                                {isOwner && (
                                                    <>
                                                        {c.status !== "archived" && <DropdownMenuSeparator />}
                                                        {/* Publish: draft → active (sends invitations) */}
                                                        {c.status === "draft" && (
                                                            <DropdownMenuItem
                                                                className="text-emerald-700 focus:text-emerald-700"
                                                                onSelect={() => setStatusTarget({ campaign: c, nextStatus: "active" })}
                                                            >
                                                                Publish campaign
                                                            </DropdownMenuItem>
                                                        )}
                                                        {/* Reactivate: archived → active */}
                                                        {c.status === "archived" && (
                                                            <DropdownMenuItem
                                                                className="text-emerald-700 focus:text-emerald-700"
                                                                onSelect={() => setStatusTarget({ campaign: c, nextStatus: "active" })}
                                                            >
                                                                Reactivate campaign
                                                            </DropdownMenuItem>
                                                        )}
                                                        {/* Convert to draft: active → draft */}
                                                        {c.status === "active" && (
                                                            <DropdownMenuItem
                                                                onSelect={() => setStatusTarget({ campaign: c, nextStatus: "draft" })}
                                                            >
                                                                Convert to draft
                                                            </DropdownMenuItem>
                                                        )}
                                                        {/* Archive: available for active + draft */}
                                                        {c.status !== "archived" && (
                                                            <DropdownMenuItem
                                                                className="text-amber-700 focus:text-amber-700"
                                                                onSelect={() => setStatusTarget({ campaign: c, nextStatus: "archived" })}
                                                            >
                                                                Archive
                                                            </DropdownMenuItem>
                                                        )}
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );

    return (
        <TrainingAuthLayout>
            <div>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Training Campaigns</h1>
                        <p className="text-sm text-gray-600 mt-1">AI roleplay training for your team.</p>
                    </div>
                    {canWrite && (
                        <Button
                            onClick={() => navigate("/training/campaigns/new")}
                            disabled={isTrainingCampaignBlocked}
                            title={campaignBlockedTitle}
                            className="flex-shrink-0"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Create Campaign
                        </Button>
                    )}
                </div>

                {/* Tab bar */}
                {!error && !loading && (
                    <div className="flex border-b border-gray-200 mb-4">
                        {(["active", "inactive"] as TabFilter[]).map((tab) => {
                            const count = tab === "active" ? activeCampaigns.length : inactiveCount;
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setTabFilter(tab)}
                                    className={cn(
                                        "py-2.5 px-4 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2",
                                        tabFilter === tab
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700",
                                    )}
                                >
                                    <>
                                        {tab === "active" ? (
                                            <PlayCircleIcon className="w-3.5 h-3.5" />
                                        ) : (
                                            <ArchiveIcon className="w-3.5 h-3.5" />
                                        )}
                                        {tab === "active" ? "Active Campaigns" : "Inactive Campaigns"}
                                    </>
                                    <span className={cn(
                                        "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums",
                                        tabFilter === tab
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-500",
                                    )}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Search + TL filter bar — always shown when campaigns exist */}
                {!error && !loading && campaigns.length > 0 && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="relative flex-1">
                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search campaigns..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <Select value={tlFilter || TL_NONE} onValueChange={(v) => setTlFilter(v === TL_NONE ? "" : v)}>
                            <SelectTrigger className="w-[200px] text-sm flex-shrink-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={TL_NONE}>All team leaders</SelectItem>
                                {tlFilterOptions.map((opt) => (
                                    <SelectItem key={opt.name} value={opt.name}>
                                        {opt.name}{" "}
                                        <span className="text-gray-400">({opt.count})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {error ? (
                    <TrainingErrorCard
                        title="We couldn't load your campaigns."
                        onRetry={() => setReloadTick((t) => t + 1)}
                    />
                ) : loading ? (
                    <Card>
                        <div className="p-5">
                            <TrainingSkeletonRows rows={5} />
                        </div>
                    </Card>
                ) : tabFilter === "active" ? (
                    activeSorted.length === 0 ? (
                        <Card>
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <div className="bg-gray-100 p-4 rounded-full mb-4">
                                    <PlayCircleIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-sm font-medium text-gray-900 mb-2">
                                    No active campaigns.
                                </h3>
                                <p className="text-xs text-gray-500 max-w-xs mb-5">
                                    Create your first training campaign to start coaching your team.
                                </p>
                                {canWrite && (
                                    <Button
                                        onClick={() => navigate("/training/campaigns/new")}
                                        disabled={isTrainingCampaignBlocked}
                                        title={campaignBlockedTitle}
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Create Campaign
                                    </Button>
                                )}
                            </div>
                        </Card>
                    ) : filteredActive.length === 0 ? (
                        <Card>
                            <div className="px-6 py-10 text-center text-sm text-gray-500 italic">
                                No campaigns match your search.
                            </div>
                        </Card>
                    ) : (
                        <>
                            {renderCampaignTable(pagedActive)}
                            <Pagination
                                currentPage={activePage}
                                totalPages={Math.ceil(filteredActive.length / CAMPAIGNS_PER_PAGE)}
                                total={filteredActive.length}
                                pageSize={CAMPAIGNS_PER_PAGE}
                                itemLabel="campaign"
                                onPageChange={setActivePage}
                                className="mt-4"
                            />
                        </>
                    )
                ) : inactiveCount === 0 ? (
                    <Card>
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                            <div className="bg-gray-100 p-4 rounded-full mb-4">
                                <ArchiveIcon className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">
                                No inactive campaigns.
                            </h3>
                            <p className="text-xs text-gray-500 max-w-xs">
                                Drafts and archived campaigns will appear here.
                            </p>
                        </div>
                    </Card>
                ) : (
                    <div className="space-y-8">
                        {/* Drafts — pre-launch, editable; not yet sending invites */}
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <h2 className="text-sm font-semibold text-gray-900">Drafts</h2>
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums bg-yellow-100 text-yellow-800">
                                    {(searchQuery || tlFilter) ? filteredDraft.length : draftSorted.length}
                                </span>
                            </div>
                            {draftSorted.length === 0 ? (
                                <p className="text-sm text-gray-500">No draft campaigns.</p>
                            ) : filteredDraft.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">No campaigns match your search.</p>
                            ) : (
                                <>
                                    {renderCampaignTable(pagedDraft)}
                                    <Pagination
                                        currentPage={draftPage}
                                        totalPages={Math.ceil(filteredDraft.length / CAMPAIGNS_PER_PAGE)}
                                        total={filteredDraft.length}
                                        pageSize={CAMPAIGNS_PER_PAGE}
                                        itemLabel="campaign"
                                        onPageChange={setDraftPage}
                                        className="mt-4"
                                    />
                                </>
                            )}
                        </section>
                        {/* Archived — retired, read-only */}
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <h2 className="text-sm font-semibold text-gray-900">Archived</h2>
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums bg-gray-100 text-gray-500">
                                    {(searchQuery || tlFilter) ? filteredArchived.length : archivedSorted.length}
                                </span>
                            </div>
                            {archivedSorted.length === 0 ? (
                                <p className="text-sm text-gray-500">No archived campaigns.</p>
                            ) : filteredArchived.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">No campaigns match your search.</p>
                            ) : (
                                <>
                                    {renderCampaignTable(pagedArchived)}
                                    <Pagination
                                        currentPage={archivedPage}
                                        totalPages={Math.ceil(filteredArchived.length / CAMPAIGNS_PER_PAGE)}
                                        total={filteredArchived.length}
                                        pageSize={CAMPAIGNS_PER_PAGE}
                                        itemLabel="campaign"
                                        onPageChange={setArchivedPage}
                                        className="mt-4"
                                    />
                                </>
                            )}
                        </section>
                    </div>
                )}
            </div>

            {/* Edit Campaign drawer */}
            <Sheet open={!!editTarget} onOpenChange={(open) => { if (!open) closeEdit(); }}>
                {/* p-0 gap-0 overrides the SheetContent base p-6/gap-4 so we control
                    padding explicitly per section. pr-12 on the header gives clearance
                    for the close button (absolute right-4 top-4 ~32px from right). */}
                <SheetContent className="sm:max-w-2xl flex flex-col h-full p-0 gap-0">
                    <SheetHeader className="px-6 pt-6 pb-4 pr-12 border-b border-gray-100 flex-shrink-0">
                        <SheetTitle>Edit Campaign</SheetTitle>
                        {editTarget && (
                            <SheetDescription>
                                {TRAINING_TYPE_LABEL[editTarget.training_type]} · {editTarget.persona_name ?? pickPersonaName(editTarget.persona_id)}
                            </SheetDescription>
                        )}
                    </SheetHeader>

                    <div className="flex-1 overflow-y-auto space-y-6 px-6 py-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Campaign name
                            </label>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value.slice(0, 60))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Team leader
                            </label>
                            {teamLeaderOptions.length > 0 ? (
                                <Select
                                    value={editTeamLeader || undefined}
                                    onValueChange={setEditTeamLeader}
                                >
                                    <SelectTrigger className="w-full text-sm">
                                        <SelectValue placeholder="Select a team leader..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {teamLeaderOptions.map((tl) => (
                                            <SelectItem key={tl.user_id} value={tl.name}>{tl.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <input
                                    type="text"
                                    value={editTeamLeader}
                                    onChange={(e) => setEditTeamLeader(e.target.value)}
                                    placeholder="Team leader name"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            )}
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">
                                {editTarget && editTarget.status !== "draft" ? "Scoring rubric" : "Scoring weights"}
                            </p>
                            {editTarget && editTarget.status !== "draft" && (
                                <p className="mb-3 text-xs text-gray-400">
                                    The scoring rubric is locked once the campaign is Active. Set it back to draft to make changes.
                                </p>
                            )}
                            {rubricLoading ? (
                                <TrainingSkeletonRows rows={5} />
                            ) : (
                                <RubricEditor
                                    value={editRubric}
                                    onChange={setEditRubric}
                                    showOnlyWeights={editTarget?.status === "draft"}
                                    readOnly={editTarget?.status !== "draft"}
                                    stacked
                                    trainingType={editTarget?.training_type}
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex-shrink-0 px-6 pt-4 pb-6 border-t border-gray-100">
                        {saveError && (
                            <p className="text-xs text-red-600 mb-2">{saveError}</p>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={closeEdit} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!canSave || saving || isTrainingCampaignBlocked}
                                title={campaignBlockedTitle}
                            >
                                {saving ? "Saving..." : "Save changes"}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Status-change confirm dialog — covers Archive, Reactivate, and
                Convert to draft. Copy + icon adapt to the target status. */}
            <Dialog open={!!statusTarget} onOpenChange={(open) => { if (!open) setStatusTarget(null); }}>
                <DialogContent className="sm:max-w-md p-6" closeButtonClassName="top-4 right-4">
                    {(() => {
                        if (!statusTarget) return null;
                        const { campaign, nextStatus } = statusTarget;
                        const config = campaign.status === "draft" && nextStatus === "active"
                            ? PUBLISH_COPY
                            : STATUS_CHANGE_COPY[nextStatus];
                        return (
                            <>
                                <DialogHeader className="flex flex-row items-center gap-3 text-left">
                                    <div className={cn(
                                        "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
                                        config.iconBg,
                                    )}>
                                        <config.icon className={cn("h-5 w-5", config.iconColor)} />
                                    </div>
                                    <DialogTitle className="text-lg font-semibold">
                                        {config.title}
                                    </DialogTitle>
                                </DialogHeader>
                                <DialogDescription className="text-sm text-gray-700">
                                    <span className="font-medium text-gray-900">{campaign.name}</span>{" "}
                                    {config.body}
                                    {config.body2 && (
                                        <span className="mt-2 block">{config.body2}</span>
                                    )}
                                </DialogDescription>
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button variant="outline" onClick={() => setStatusTarget(null)} disabled={statusChanging}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleStatusChange} disabled={statusChanging}>
                                        {statusChanging ? `${config.actionGerund}...` : config.actionLabel}
                                    </Button>
                                </div>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
            {savedToast && (
                <div className="fixed bottom-6 right-6 z-50 rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white shadow-lg">
                    {savedToast}
                </div>
            )}
        </TrainingAuthLayout>
    );
}

function SortableTh({
    label,
    sortKey,
    current,
    dir,
    onSort,
    align = "left",
    disabled = false,
    widthClass,
}: {
    label: string;
    sortKey: SortKey;
    current: SortKey;
    dir: SortDir;
    onSort: (key: SortKey) => void;
    align?: "left" | "center";
    disabled?: boolean;
    widthClass?: string;
}) {
    const active = !disabled && current === sortKey;
    const justify = align === "center" ? "justify-center" : "justify-start";
    return (
        <th
            className={cn(
                "h-[45px] px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider",
                !disabled && "cursor-pointer select-none hover:text-gray-800",
                align === "center" ? "text-center" : "text-left",
                widthClass
            )}
            onClick={() => !disabled && onSort(sortKey)}
        >
            <div className={cn("flex items-center gap-1", justify)}>
                {label}
                {!disabled && (
                    active ? (
                        dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )
                )}
            </div>
        </th>
    );
}
